import {
    AlertTriangle,
    ArrowRight,
    CalendarDays,
    ChevronLeft,
    CheckCircle2,
    IndianRupee,
    LoaderCircle,
    NotebookText,
    Percent,
    ReceiptText,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CreateAdvanceRequestScreen.module.css'

const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const getNextMonthCycleStart = () => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().split('T')[0]
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '—'
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const CreateAdvanceRequestScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { staffIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [staffName, setStaffName] = useState(location?.state?.staffName || 'Staff')

    const [formData, setFormData] = useState({
        principalAmount: '',
        interestRate: 0,
        repaymentType: 'FIXED_EMI',
        emiAmount: '',
        emiPercent: '',
        repaymentStartCycle: getNextMonthCycleStart(),
        notes: '',
    })
    const [preview, setPreview] = useState(null)
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const fetchStaffName = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !staffIdentifier) return

        try {
            const response = await fetch(BASE_URL_API + 'getStaffAdvanceLedger', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (respo?.response?.staffName) {
                    setStaffName(respo.response.staffName)
                }
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch {
        }
    }, [authCtx, activeStore?.storeIdentifier, staffIdentifier])

    useEffect(() => {
        if (!staffIdentifier) return
        if (location?.state?.staffName) return
        fetchStaffName()
    }, [staffIdentifier, location?.state?.staffName, fetchStaffName])

    const set = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        setErrors((prev) => ({ ...prev, [field]: undefined }))
        if (preview) setPreview(null)
    }

    // Live tenure helper
    const tenureMonths = useMemo(() => {
        if (formData.repaymentType !== 'FIXED_EMI') return null
        const p = parseFloat(formData.principalAmount)
        const e = parseFloat(formData.emiAmount)
        if (!isFinite(p) || !isFinite(e) || e <= 0 || p <= 0) return null
        return Math.ceil(p / e)
    }, [formData.principalAmount, formData.emiAmount, formData.repaymentType])

    const createAdvanceRequest = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        const newErrors = {}
        if (!formData.principalAmount || parseFloat(formData.principalAmount) <= 0) {
            newErrors.principalAmount = 'Enter a valid advance amount.'
        }
        if (formData.repaymentType === 'FIXED_EMI') {
            if (!formData.emiAmount || parseFloat(formData.emiAmount) <= 0) {
                newErrors.emiAmount = 'Enter a monthly EMI amount.'
            } else if (parseFloat(formData.emiAmount) > parseFloat(formData.principalAmount)) {
                newErrors.emiAmount = 'EMI cannot exceed the advance amount.'
            }
        }
        if (formData.repaymentType === 'PERCENT_OF_NET') {
            if (!formData.emiPercent || parseFloat(formData.emiPercent) <= 0) {
                newErrors.emiPercent = 'Enter a deduction percentage.'
            }
        }
        if (!formData.repaymentStartCycle) {
            newErrors.repaymentStartCycle = 'Select a start cycle.'
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})

        try {
            setIsSaving(true)
            const response = await fetch(BASE_URL_API + 'createStaffAdvancePaymentRequest', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier,
                    principalAmount: parseFloat(formData.principalAmount),
                    interestRate: parseFloat(formData.interestRate) || 0,
                    repaymentType: formData.repaymentType,
                    emiAmount: parseFloat(formData.emiAmount) || 0,
                    emiPercent: parseFloat(formData.emiPercent) || 0,
                    repaymentStartCycle: formData.repaymentStartCycle,
                    notes: formData.notes.trim(),
                }),
            })
            const respo = await response.json()

            if (respo?.status === 'success') {
                respo.response?.warnings?.forEach((w) => toast.warning(w.message))
                setPreview(respo.response)
            } else {
                if (respo?.message === 'tokenExpired') { authCtx.logout(); return }
                switch (respo?.errorCode) {
                    case 'VAL_001':
                        if (respo.message?.includes('emiAmount')) {
                            setErrors((prev) => ({ ...prev, emiAmount: respo.message }))
                        } else if (respo.message?.includes('emiPercent')) {
                            setErrors((prev) => ({ ...prev, emiPercent: respo.message }))
                        } else {
                            toast.error(respo.message)
                        }
                        break
                    case 'VAL_002':
                        setErrors((prev) => ({ ...prev, principalAmount: respo.message }))
                        break
                    case 'ADV_006':
                        setErrors((prev) => ({ ...prev, emiAmount: respo.message }))
                        break
                    case 'VAL_005':
                        setErrors((prev) => ({ ...prev, repaymentStartCycle: respo.message }))
                        break
                    case 'VAL_007':
                        toast.error('Staff not found.')
                        navigate('/payroll/advance-book')
                        break
                    case 'VAL_008':
                        toast.error('No salary assigned to this staff. Assign salary first.')
                        break
                    default:
                        toast.error(respo?.message || 'Failed to create advance request.')
                }
            }
        } catch (err) {
            console.error('createAdvanceRequest error:', err)
            toast.error('Network error.')
        } finally {
            setIsSaving(false)
        }
    }

    const handleConfirmSubmit = () => {
        toast.success('Advance request created. Pending your approval.')
        navigate(`/payroll/advance/staff-ledger/${staffIdentifier}`, {
            state: { staffName },
        })
    }

    if (!staffIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={15} />
                    <span>Open this screen from a staff advance ledger.</span>
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={() => navigate('/payroll/advance-book')}
                    >
                        Go to Advance Book
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => navigate(-1)}
                    disabled={isSaving}
                >
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <ReceiptText size={11} />
                        Payroll · Advances
                    </span>
                    <h1 className={styles.headerTitle}>New Advance Request</h1>
                    <p className={styles.headerSub}>for {staffName}</p>
                </div>
            </div>

            {/* ── Form card ── */}
            {!preview ? (
                <div className={styles.formCard}>

                    {/* Amount */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            <IndianRupee size={13} /> Advance Amount *
                        </label>
                        <input
                            type="number"
                            className={`${styles.textInput} ${errors.principalAmount ? styles.inputError : ''}`}
                            placeholder="e.g. 10000"
                            value={formData.principalAmount}
                            onChange={(e) => set('principalAmount', e.target.value)}
                            min="0"
                        />
                        {errors.principalAmount && (
                            <span className={styles.errorText}>{errors.principalAmount}</span>
                        )}
                    </div>

                    {/* Interest Rate */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            <Percent size={13} /> Interest Rate (% per year)
                        </label>
                        <input
                            type="number"
                            className={styles.textInput}
                            placeholder="0"
                            value={formData.interestRate}
                            onChange={(e) => set('interestRate', e.target.value)}
                            min="0"
                            step="0.1"
                        />
                        <span className={styles.fieldHint}>Leave 0 for interest-free advance</span>
                    </div>

                    {/* Repayment Type Toggle */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Repayment Type</label>
                        <div className={styles.toggleRow}>
                            <button
                                type="button"
                                className={`${styles.toggleBtn} ${
                                    formData.repaymentType === 'FIXED_EMI' ? styles.toggleBtnActive : ''
                                }`}
                                onClick={() => set('repaymentType', 'FIXED_EMI')}
                            >
                                Fixed EMI
                            </button>
                            <button
                                type="button"
                                className={`${styles.toggleBtn} ${
                                    formData.repaymentType === 'PERCENT_OF_NET' ? styles.toggleBtnActive : ''
                                }`}
                                onClick={() => set('repaymentType', 'PERCENT_OF_NET')}
                            >
                                % of Net Salary
                            </button>
                        </div>
                    </div>

                    {/* FIXED_EMI fields */}
                    {formData.repaymentType === 'FIXED_EMI' && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                <IndianRupee size={13} /> Monthly EMI Amount *
                            </label>
                            <input
                                type="number"
                                className={`${styles.textInput} ${errors.emiAmount ? styles.inputError : ''}`}
                                placeholder="e.g. 2000"
                                value={formData.emiAmount}
                                onChange={(e) => set('emiAmount', e.target.value)}
                                min="0"
                            />
                            {errors.emiAmount ? (
                                <span className={styles.errorText}>{errors.emiAmount}</span>
                            ) : tenureMonths !== null ? (
                                <span className={styles.fieldHint}>
                                    Tenure: <strong>{tenureMonths} month{tenureMonths !== 1 ? 's' : ''}</strong>
                                </span>
                            ) : null}
                        </div>
                    )}

                    {/* PERCENT_OF_NET fields */}
                    {formData.repaymentType === 'PERCENT_OF_NET' && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                <Percent size={13} /> Deduction % per Month *
                            </label>
                            <input
                                type="number"
                                className={`${styles.textInput} ${errors.emiPercent ? styles.inputError : ''}`}
                                placeholder="e.g. 20"
                                value={formData.emiPercent}
                                onChange={(e) => set('emiPercent', e.target.value)}
                                min="0"
                                max="100"
                                step="0.1"
                            />
                            {errors.emiPercent ? (
                                <span className={styles.errorText}>{errors.emiPercent}</span>
                            ) : (
                                <span className={styles.fieldHint}>
                                    Deducted monthly as a % of net salary
                                </span>
                            )}
                        </div>
                    )}

                    {/* Start Cycle */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            <CalendarDays size={13} /> Deductions start from *
                        </label>
                        <input
                            type="date"
                            className={`${styles.textInput} ${errors.repaymentStartCycle ? styles.inputError : ''}`}
                            value={formData.repaymentStartCycle}
                            onChange={(e) => set('repaymentStartCycle', e.target.value)}
                        />
                        {errors.repaymentStartCycle && (
                            <span className={styles.errorText}>{errors.repaymentStartCycle}</span>
                        )}
                    </div>

                    {/* Notes */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            <NotebookText size={13} /> Reason for Advance
                        </label>
                        <textarea
                            className={styles.textArea}
                            placeholder="e.g. Medical emergency, Home repair"
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => set('notes', e.target.value)}
                        />
                    </div>

                    <button
                        type="button"
                        className={styles.previewBtn}
                        onClick={createAdvanceRequest}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <><LoaderCircle size={15} className={styles.spinIcon} /> Calculating...</>
                        ) : (
                            <>Calculate &amp; Preview <ArrowRight size={14} /></>
                        )}
                    </button>
                </div>
            ) : (
                /* ── Schedule Preview Card ── */
                <div className={styles.previewCard}>
                    <div className={styles.previewHeader}>
                        <CheckCircle2 size={17} className={styles.previewCheckIcon} />
                        <span className={styles.previewTitle}>Repayment Schedule</span>
                    </div>

                    <div
                        className={`${styles.affordabilityRow} ${
                            (preview?.emiAsPercentOfSalary || 0) > 40
                                ? styles.affordabilityHigh
                                : styles.affordabilityOk
                        }`}
                    >
                        {(preview?.emiAsPercentOfSalary || 0) > 40 ? '⚠ High burden' : '✓ Affordable'} —
                        EMI is <strong>{(preview?.emiAsPercentOfSalary || 0).toFixed(1)}%</strong> of basic salary
                    </div>

                    {preview?.repaymentType === 'FIXED_EMI' && preview?.totalEmiCount > 0 && (
                        <div className={styles.previewMeta}>
                            <span className={styles.previewMetaItem}>
                                {preview.totalEmiCount} instalments
                            </span>
                            <span className={styles.previewMetaItem}>
                                ₹{formatINR(preview.emiAmount)} / month
                            </span>
                            {preview.firstEmiCycle && (
                                <span className={styles.previewMetaItem}>
                                    Starts {preview.firstEmiCycle}
                                </span>
                            )}
                        </div>
                    )}

                    <div className={styles.scheduleList}>
                        <div className={styles.scheduleHeadRow}>
                            <span>EMI</span>
                            <span>Cycle</span>
                            <span>Amount</span>
                        </div>
                        {Array.isArray(preview?.repaymentSchedulePreview) &&
                            preview.repaymentSchedulePreview.map((row) => (
                                <div key={row.emiSequence} className={styles.scheduleRow}>
                                    <span className={styles.emiSeq}>#{row.emiSequence}</span>
                                    <span className={styles.emiCycle}>
                                        {MONTH_NAMES[(row.dueCycleMonth || 1) - 1]} {row.dueCycleYear}
                                        {row.isEstimated ? (
                                            <span className={styles.estBadge}>est.</span>
                                        ) : null}
                                    </span>
                                    <span className={styles.emiAmount}>₹{formatINR(row.dueAmount)}</span>
                                </div>
                            ))}
                    </div>

                    <div className={styles.previewActions}>
                        <button
                            type="button"
                            className={styles.editBtn}
                            onClick={() => setPreview(null)}
                        >
                            Edit Details
                        </button>
                        <button
                            type="button"
                            className={styles.confirmBtn}
                            onClick={handleConfirmSubmit}
                        >
                            Submit Request <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CreateAdvanceRequestScreen